import {
  getProviderFrameworkOptions,
  normalizeProviderComplianceFrameworks,
} from "../../utils/providerFrameworks.js";
import { getProviderMeta, resolveProviderId } from "../../utils/providerContext.js";

/**
 * Multi-select compliance framework picker — catalog-driven per provider (IMPL.5.5).
 *
 * @param {{ providerId?: string, value?: string[], onChange?: (next: string[]) => void }} props
 */
export default function ProviderFrameworkPicker({ providerId = "entra", value, onChange }) {
  const id = resolveProviderId({ providerId }) || "entra";
  const meta = getProviderMeta(id);
  const fw = meta.frameworks;
  const options = getProviderFrameworkOptions(id);
  const set = new Set(Array.isArray(value) ? value : []);

  const toggle = (frameworkId) => {
    const next = new Set(set);
    if (next.has(frameworkId)) next.delete(frameworkId);
    else next.add(frameworkId);
    const baselineId = fw?.baselineFrameworkId;
    if (baselineId) {
      const hasSpecific = [...next].some((x) => x !== baselineId);
      if (hasSpecific) next.delete(baselineId);
    } else {
      next.delete("OKTA-BASELINE");
    }
    onChange?.([...next]);
  };

  const toggleBaseline = () => {
    const baselineId = fw?.baselineFrameworkId;
    if (!baselineId) return;
    const next = new Set(set);
    if (next.has(baselineId)) next.delete(baselineId);
    else {
      next.clear();
      next.add(baselineId);
    }
    onChange?.([...next]);
  };

  const standardOptions = options.filter((o) => !o.isBaseline);
  const baselineOption = options.find((o) => o.isBaseline);

  return (
    <div style={{ display: "grid", gap: "8px" }}>
      {standardOptions.map((o) => (
        <label
          key={o.id}
          style={{ display: "flex", gap: "10px", alignItems: "flex-start", fontSize: "13px" }}
        >
          <input type="checkbox" checked={set.has(o.id)} onChange={() => toggle(o.id)} />
          <span style={{ color: "var(--text-secondary)" }}>
            <span style={{ display: "block", color: "var(--text-primary)" }}>{o.label}</span>
            {o.description ? (
              <span className="text-dim" style={{ fontSize: "11px" }}>
                {o.description}
              </span>
            ) : null}
          </span>
        </label>
      ))}
      {baselineOption ? (
        <label
          style={{ display: "flex", gap: "10px", alignItems: "flex-start", fontSize: "13px", marginTop: "4px" }}
        >
          <input type="checkbox" checked={set.has(baselineOption.id)} onChange={toggleBaseline} />
          <span style={{ color: "var(--text-secondary)" }}>
            <span style={{ display: "block", color: "var(--text-primary)" }}>{baselineOption.label}</span>
            <span className="text-dim" style={{ fontSize: "11px" }}>
              {baselineOption.description}
            </span>
          </span>
        </label>
      ) : null}
      <p className="text-dim" style={{ fontSize: "11px", marginTop: "4px" }}>
        {fw?.pickerHint}
      </p>
    </div>
  );
}

/**
 * @param {string | null | undefined} providerId
 * @param {string[] | null | undefined} list
 */
export function normalizeFrameworksForProviderSubmit(providerId, list) {
  return normalizeProviderComplianceFrameworks(providerId, list);
}
